import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useLabelIngestionDocumentClassificationMutation } from '@client/graphql/__generated__/document-operations';
import { InternalGetIngestionDocumentFragment } from '@client/graphql/__generated__/types';
import { filter, map, sortBy, sumBy } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DocumentSelect,
  DocumentSelectOption,
} from '~/services/document/common/DocumentSelect';
import {
  DocumentVersionSelect,
  DocumentVersionSelectOption,
} from '~/services/document/common/DocumentVersionSelect';
import { PDFScrollable } from '~/services/document/components/PDFScrollable/PDFScrollable';
import { VisiblePage } from '~/services/document/components/PDFScrollable/types';
import { DocumentClassification } from './classification.types';

type AlertStatus = 'success' | 'error' | 'warning';

interface PageSelection {
  pageNumber: number;
  isSelected: boolean;
  documentVersionId?: string;
  currentClassification?: DocumentClassification;
  isLocked?: boolean;
}

interface ClassificationOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingestionDocument: InternalGetIngestionDocumentFragment;
  classification: DocumentClassification[];
}

interface MappingRow {
  id: string;
  sourcePages: number[];
  document?: DocumentSelectOption | null;
  version?: DocumentVersionSelectOption | null;
  targetStartPage?: number;
}

const SCORE_THRESHOLDS = {
  HIGH_SUCCESS: 0.94,
  SUCCESS: 0.85,
  WARNING: 0.6,
} as const;

const FORM_NAMES = {
  UNKNOWN: 'UNKNOWN',
} as const;

const DEFAULT_PAGE_COUNT = 10;

const filterPages = (
  pages: PageSelection[],
  filterType: 'unknown' | 'classified' | 'highConfidence' | 'selected'
): PageSelection[] => {
  switch (filterType) {
    case 'unknown':
      return filter(
        pages,
        (p) => p.currentClassification?.formName === FORM_NAMES.UNKNOWN
      );
    case 'classified':
      return pages.filter(
        (p) =>
          p.currentClassification &&
          p.currentClassification.formName !== FORM_NAMES.UNKNOWN
      );
    case 'selected':
      return filter(pages, 'isSelected');
    default:
      return [];
  }
};

export const ClassificationOverrideModal: FC<
  ClassificationOverrideModalProps
> = ({ isOpen, onClose, ingestionDocument, classification }) => {
  const { id: ingestionDocumentId, fileUrl: ingestionDocumentUrl } =
    ingestionDocument;
  const [pageSelections, setPageSelections] = useState<PageSelection[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [statusType, setStatusType] = useState<AlertStatus | null>(null);
  const [visiblePage, setVisiblePage] = useState<VisiblePage | null>(null);

  // Mapping rows allow per-page overrides and bulk operations
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [labelClassificationMutation] =
    useLabelIngestionDocumentClassificationMutation();

  // Keep page lock state in the pageSelections array, derived from mappingRows
  useEffect(() => {
    setPageSelections((prev) => {
      if (!prev || prev.length === 0) return prev;
      const mapped = new Set<number>();
      for (const row of mappingRows) {
        for (const p of row.sourcePages) mapped.add(p);
      }
      let changed = false;
      const next = prev.map((p) => {
        const locked = mapped.has(p.pageNumber);
        if (p.isLocked !== locked) {
          changed = true;

          return { ...p, isLocked: locked };
        }

        return p;
      });

      return changed ? next : prev;
    });
  }, [mappingRows]);

  const selectedPagesToOverride = useMemo(
    () => filterPages(pageSelections, 'selected'),
    [pageSelections]
  );

  const handleClearAll = useCallback(() => {
    setPageSelections((prev) =>
      map(prev, (page) => ({ ...page, isSelected: false }))
    );
    setMappingRows([]);
  }, []);

  const handleToggleUnknownPages = useCallback(() => {
    const unknownPages = filterPages(pageSelections, 'unknown');
    const allUnknownSelected = unknownPages.every((p) => p.isSelected);

    setPageSelections((prev) =>
      map(prev, (page) => {
        const isUnknown =
          page.currentClassification?.formName === FORM_NAMES.UNKNOWN;
        if (!isUnknown) return page;

        // Do not toggle pages already mapped
        const isMapped = !!page.isLocked;

        if (allUnknownSelected) {
          return { ...page, isSelected: false };
        }

        return { ...page, isSelected: isMapped ? false : true };
      })
    );
  }, [pageSelections]);

  const createPageSelections = (
    classification: DocumentClassification[]
  ): PageSelection[] => {
    const classificationsByPage = new Map<number, DocumentClassification>();
    classification.forEach((item) => {
      classificationsByPage.set(item.pageNumber, item);
    });

    const pageCount =
      classification.length > 0
        ? Math.max(...map(classification, 'pageNumber'))
        : DEFAULT_PAGE_COUNT;

    return Array.from({ length: pageCount }, (_, i) => {
      const pageNumber = i + 1;

      return {
        pageNumber,
        isSelected: false,
        currentClassification: classificationsByPage.get(pageNumber),
        isLocked: false,
      };
    });
  };

  useEffect(() => {
    if (ingestionDocumentUrl && isOpen && classification) {
      setPageSelections(createPageSelections(classification));
      setMappingRows([]);
    }
  }, [ingestionDocumentUrl, isOpen, classification]);

  const pdfFiles = ingestionDocumentUrl
    ? [
        {
          url: ingestionDocumentUrl,
          name: ingestionDocument.documentName || 'Document',
        },
      ]
    : [];

  const handlePageSelectionChange = useCallback(
    (pageNumber: number, isSelected: boolean) => {
      // Ignore changes for mapped pages
      if (pageSelections.find((p) => p.pageNumber === pageNumber)?.isLocked)
        return;
      setPageSelections((prev) =>
        prev.map((page) =>
          page.pageNumber === pageNumber ? { ...page, isSelected } : page
        )
      );
    },
    [pageSelections]
  );

  const handleClose = useCallback(() => {
    setPageSelections([]);
    setIsSubmitting(false);
    setStatusMessage('');
    setStatusType(null);
    setMappingRows([]);
    onClose();
  }, [onClose]);

  const totalMappedPages = useMemo(
    () => sumBy(mappingRows, (row) => row.sourcePages.length),
    [mappingRows]
  );

  const addRowFromSelected = useCallback(() => {
    const sourcePageNumbers = sortBy(
      map(selectedPagesToOverride, 'pageNumber')
    );
    if (sourcePageNumbers.length === 0) return;

    const id = Math.random().toString(36).slice(2);
    setMappingRows((prev) => [
      ...prev,
      {
        id,
        sourcePages: sourcePageNumbers,
        document: null,
        version: null,
        targetStartPage: undefined,
      },
    ]);
    setPageSelections((prev) =>
      map(prev, (p) => ({ ...p, isSelected: false }))
    );
  }, [selectedPagesToOverride]);

  const updateRow = useCallback((id: string, patch: Partial<MappingRow>) => {
    setMappingRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  }, []);

  const removeRow = useCallback((id: string) => {
    setMappingRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsConfirmOpen(false);
    setStatusMessage('');
    setStatusType(null);

    if (mappingRows.length === 0) {
      setStatusMessage('Add at least one mapping row.');
      setStatusType('warning');

      return;
    }

    for (const row of mappingRows) {
      if (!row.version?.value || !row.targetStartPage) {
        setStatusMessage('Each row requires a version and target start page.');
        setStatusType('warning');

        return;
      }
    }

    setIsSubmitting(true);

    try {
      const orderedRows = sortBy(mappingRows, (row) => row.sourcePages[0] ?? 0);
      const documentVersionPagePairs = orderedRows.flatMap((row) => {
        const start = row.targetStartPage as number;
        const sortedSourcePages = sortBy(row.sourcePages);

        return map(sortedSourcePages, (sourcePage, idx) => ({
          documentVersionId: row.version!.value,
          pageNumber: start + idx,
          sourcePageNumber: sourcePage,
        }));
      });

      const result = await labelClassificationMutation({
        variables: {
          input: {
            ingestionDocumentId,
            documentVersionPagePairs,
          },
        },
      });

      if (result.data?.labelIngestionDocumentClassification) {
        // Notify listeners that an override was submitted for this document
        try {
          window.dispatchEvent(
            new CustomEvent('classificationOverrideSubmitted', {
              detail: { ingestionDocumentId },
            })
          );
        } catch {
          // noop – event dispatch is best-effort
        }
        handleClose();
      } else {
        throw new Error('Unknown error occurred');
      }
    } catch (error) {
      setStatusMessage(
        error instanceof Error
          ? error.message
          : 'Failed to override classification'
      );
      setStatusType('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    mappingRows,
    labelClassificationMutation,
    ingestionDocumentId,
    handleClose,
  ]);

  return (
    <Modal isOpen={isOpen} size="full" onClose={handleClose}>
      <ModalOverlay />
      <ModalContent maxH="95vh" maxW="95vw">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Flex align="center" justify="space-between" w="full">
              <Text>Override Classification - Select Pages</Text>
            </Flex>
            {ingestionDocument.documentName && (
              <Text color="compliance.text.secondary" fontSize="sm">
                {ingestionDocument.documentName}
              </Text>
            )}
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0}>
          <Flex height="calc(95vh - 120px)" width="100%">
            {/* PDF Viewer Section */}
            {ingestionDocumentUrl && pdfFiles.length > 0 && (
              <Box
                bg="compliance.pdf.bg"
                borderColor="compliance.drawer.border"
                borderRight="1px solid"
                position="relative"
                width="60%"
              >
                <PDFScrollable
                  defaultZoom={1}
                  maxWidth={800}
                  pdfFiles={pdfFiles}
                  onVisiblePageChange={setVisiblePage}
                />
              </Box>
            )}

            {/* Page Selection Section */}
            <Box
              overflowY="auto"
              p={4}
              width={ingestionDocumentUrl ? '40%' : '100%'}
            >
              <VStack align="stretch" spacing={4}>
                <StatusAlert
                  statusMessage={statusMessage}
                  statusType={statusType}
                />

                <SummaryStats pageSelections={pageSelections} />

                {/* Page Navigation Info */}
                {visiblePage && (
                  <Box
                    bg="compliance.selected.bg"
                    borderColor="compliance.selected.border"
                    borderRadius="md"
                    borderWidth="1px"
                    p={3}
                  >
                    <Text
                      color="compliance.selected.text"
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      Currently viewing: Page {visiblePage.pageNumber}
                      {pageSelections[visiblePage.pageNumber - 1]
                        ?.currentClassification && (
                        <Text
                          as="span"
                          color="compliance.selected.text"
                          fontWeight="normal"
                          ml={2}
                        >
                          •{' '}
                          {
                            pageSelections[visiblePage.pageNumber - 1]
                              ?.currentClassification?.formName
                          }{' '}
                          (
                          {(
                            (pageSelections[visiblePage.pageNumber - 1]
                              ?.currentClassification?.score || 0) * 100
                          ).toFixed(1)}
                          %)
                        </Text>
                      )}
                    </Text>
                  </Box>
                )}

                <BulkActions
                  pageSelections={pageSelections}
                  onClearAll={handleClearAll}
                  onToggleUnknownPages={handleToggleUnknownPages}
                />

                <PageSelectionTable
                  pageSelections={pageSelections}
                  onPageSelectionChange={handlePageSelectionChange}
                />

                <MappingTable
                  canAddRow={selectedPagesToOverride.length > 0}
                  mappingRows={mappingRows}
                  onAddRow={addRowFromSelected}
                  onRemoveRow={removeRow}
                  onUpdateRow={updateRow}
                />
              </VStack>
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Stack direction="row" spacing={3}>
            <Button
              isDisabled={isSubmitting}
              variant="ghost"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              _hover={{ bg: 'compliance.button.primary.hover' }}
              bg="compliance.button.primary.bg"
              color="compliance.button.primary.text"
              isDisabled={mappingRows.length === 0 || totalMappedPages === 0}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              onClick={() => setIsConfirmOpen(true)}
            >
              Override Classification ({totalMappedPages} pages)
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>

      <ConfirmationDialog
        isOpen={isConfirmOpen}
        isSubmitting={isSubmitting}
        mappingRows={mappingRows}
        totalMappedPages={totalMappedPages}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => void handleSubmit()}
      />
    </Modal>
  );
};

/**
 * Displays status alerts for user feedback during operations.
 * Shows success, warning, or error messages with appropriate styling.
 */
interface StatusAlertProps {
  statusMessage: string;
  statusType: AlertStatus | null;
}

const StatusAlert: FC<StatusAlertProps> = ({ statusMessage, statusType }) => {
  if (!statusMessage || !statusType) return null;

  return (
    <Alert status={statusType}>
      <AlertIcon />
      <AlertDescription>{statusMessage}</AlertDescription>
    </Alert>
  );
};

/**
 * Shows summary statistics about page classifications.
 * Displays total pages, unknown pages, classified pages, and high confidence pages.
 */
interface SummaryStatsProps {
  pageSelections: PageSelection[];
}

const SummaryStats: FC<SummaryStatsProps> = ({ pageSelections }) => {
  return (
    <Box
      bg="compliance.card.bg"
      borderColor="compliance.card.border"
      borderRadius="md"
      borderWidth="1px"
      p={4}
    >
      <HStack spacing={4}>
        <VStack spacing={1}>
          <Text fontSize="xl" fontWeight="bold">
            {pageSelections.length}
          </Text>
          <Text color="compliance.text.secondary" fontSize="sm">
            Total Pages
          </Text>
        </VStack>
        <Divider height="40px" orientation="vertical" />
        <VStack spacing={1}>
          <Text fontSize="xl" fontWeight="bold">
            {filterPages(pageSelections, 'unknown').length}
          </Text>
          <Text color="compliance.text.secondary" fontSize="sm">
            Unknown
          </Text>
        </VStack>
        <Divider height="40px" orientation="vertical" />
        <VStack spacing={1}>
          <Text fontSize="xl" fontWeight="bold">
            {filterPages(pageSelections, 'classified').length}
          </Text>
          <Text color="compliance.text.secondary" fontSize="sm">
            Classified
          </Text>
        </VStack>
        <Divider height="40px" orientation="vertical" />
        <VStack spacing={1}>
          <Text fontSize="xl" fontWeight="bold">
            {filterPages(pageSelections, 'highConfidence').length}
          </Text>
          <Text color="compliance.text.secondary" fontSize="sm">
            High Confidence
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

/**
 * Provides bulk action buttons for managing page selections.
 * Includes Clear All and toggle UNKNOWN pages functionality.
 */
interface BulkActionsProps {
  pageSelections: PageSelection[];
  onClearAll: () => void;
  onToggleUnknownPages: () => void;
}

const BulkActions: FC<BulkActionsProps> = ({
  pageSelections,
  onClearAll,
  onToggleUnknownPages,
}) => {
  const getUnknownPagesButtonText = (): string => {
    const unknownPages = filterPages(pageSelections, 'unknown');
    const selectedUnknownPages = filter(unknownPages, 'isSelected');
    const allUnknownSelected =
      selectedUnknownPages.length === unknownPages.length &&
      unknownPages.length > 0;

    return allUnknownSelected ? 'Deselect UNKNOWN' : 'Select UNKNOWN';
  };

  return (
    <HStack justify="flex-end" spacing={2}>
      <Button size="sm" variant="ghost" onClick={onClearAll}>
        Clear All
      </Button>
      <Button size="sm" variant="outline" onClick={onToggleUnknownPages}>
        {getUnknownPagesButtonText()}
      </Button>
    </HStack>
  );
};

/**
 * Renders the table showing individual pages with selection checkboxes.
 * Displays page numbers, classifications, form names, and confidence scores.
 */
interface PageSelectionTableProps {
  pageSelections: PageSelection[];
  onPageSelectionChange: (pageNumber: number, isSelected: boolean) => void;
}

const PageSelectionTable: FC<PageSelectionTableProps> = ({
  pageSelections,
  onPageSelectionChange,
}) => {
  const getScoreColor = (score: number): string => {
    if (score >= SCORE_THRESHOLDS.HIGH_SUCCESS)
      return 'compliance.status.success';
    if (score >= SCORE_THRESHOLDS.SUCCESS) return 'compliance.status.warning';
    if (score >= SCORE_THRESHOLDS.WARNING) return 'compliance.status.warning';

    return 'compliance.status.error';
  };

  return (
    <Box
      bg="compliance.card.bg"
      borderColor="compliance.card.border"
      borderRadius="md"
      borderWidth="1px"
      maxHeight="60vh"
      overflow="hidden"
    >
      <Box overflowY="auto" p={4}>
        <Table size="sm" variant="simple">
          <Thead position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th width="60px">Select</Th>
              <Th width="60px">Page</Th>
              <Th>Classification</Th>
              <Th width="80px">Score</Th>
            </Tr>
          </Thead>
          <Tbody>
            {map(pageSelections, (page) => (
              <Tr key={page.pageNumber}>
                <Td>
                  <Checkbox
                    isChecked={page.isSelected}
                    isDisabled={!!page.isLocked}
                    onChange={(e) =>
                      onPageSelectionChange(page.pageNumber, e.target.checked)
                    }
                  />
                </Td>
                <Td>
                  <Text fontWeight="medium">{page.pageNumber}</Text>
                </Td>
                <Td>
                  <Text
                    color="compliance.text.primary"
                    fontSize="sm"
                    fontWeight="medium"
                  >
                    {page.currentClassification?.formName ?? 'Unknown'}
                    {page.currentClassification?.version ? (
                      <Text as="span" color="compliance.text.muted">
                        {` (${page.currentClassification.version})`}
                      </Text>
                    ) : null}
                  </Text>
                </Td>

                <Td>
                  {page.currentClassification ? (
                    <Text
                      color={getScoreColor(page.currentClassification.score)}
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      {(page.currentClassification.score * 100).toFixed(1)}%
                    </Text>
                  ) : (
                    <Text color="compliance.text.disabled" fontSize="sm">
                      —
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

/**
 * Manages the document mapping rows table where users configure override mappings.
 * Each row maps source pages to target document version pages.
 */
interface MappingTableProps {
  mappingRows: MappingRow[];
  onAddRow: () => void;
  onUpdateRow: (id: string, patch: Partial<MappingRow>) => void;
  onRemoveRow: (id: string) => void;
  canAddRow: boolean;
}

const MappingTable: FC<MappingTableProps> = ({
  mappingRows,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
  canAddRow,
}) => {
  return (
    <Box
      bg="compliance.card.bg"
      borderColor="compliance.card.border"
      borderRadius="md"
      borderWidth="1px"
      p={4}
    >
      <Flex align="center" justify="space-between" mb={3}>
        <Text fontWeight="semibold">Document Mappings</Text>
        <Button
          colorScheme="blue"
          isDisabled={!canAddRow}
          size="sm"
          onClick={onAddRow}
        >
          Add from Selected
        </Button>
      </Flex>
      {mappingRows.length === 0 ? (
        <Text color="compliance.text.muted" fontSize="sm" textAlign="left">
          No mappings configured. Select pages above and click "Add from
          Selected" to create mappings.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {map(mappingRows, (row) => {
            const sortedPages = sortBy(row.sourcePages);
            const pageRangeText =
              sortedPages.length === 1
                ? sortedPages[0].toString()
                : `${sortedPages[0]}-${sortedPages[sortedPages.length - 1]}`;

            return (
              <Box
                key={row.id}
                _hover={{ shadow: 'md', borderColor: 'gray.300' }}
                bg="white"
                borderColor="gray.200"
                borderRadius="lg"
                borderWidth="1px"
                p={4}
                position="relative"
                shadow="sm"
                transition="all 0.2s"
              >
                <IconButton
                  aria-label="Remove mapping"
                  colorScheme="red"
                  icon={<CloseIcon />}
                  position="absolute"
                  right={2}
                  size="sm"
                  top={2}
                  variant="ghost"
                  onClick={() => onRemoveRow(row.id)}
                />

                <VStack align="stretch" spacing={4}>
                  {/* Header with source pages and start page in same row */}
                  <Flex align="flex-start" justify="space-between" pr={10}>
                    <Box flex="1">
                      <Text
                        color="gray.600"
                        fontSize="xs"
                        fontWeight="semibold"
                        letterSpacing="wide"
                        mb={1}
                        textTransform="uppercase"
                      >
                        Source Pages
                      </Text>
                      <Text color="gray.900" fontSize="lg" fontWeight="bold">
                        {pageRangeText}
                      </Text>
                      <Text color="gray.500" fontSize="sm">
                        {sortedPages.length} page
                        {sortedPages.length === 1 ? '' : 's'}
                      </Text>
                    </Box>
                    <Box minW="120px" ml={4}>
                      <Text
                        color="gray.600"
                        fontSize="xs"
                        fontWeight="semibold"
                        letterSpacing="wide"
                        mb={1}
                        textTransform="uppercase"
                      >
                        Mapping Start Page
                      </Text>
                      <NumberInput
                        min={1}
                        size="md"
                        value={row.targetStartPage ?? ''}
                        onChange={(_, valueAsNumber) =>
                          onUpdateRow(row.id, {
                            targetStartPage: isNaN(valueAsNumber)
                              ? undefined
                              : valueAsNumber,
                          })
                        }
                      >
                        <NumberInputField placeholder="Page #" />
                      </NumberInput>
                      <Text
                        as="span"
                        color="gray.500"
                        fontSize="xs"
                        ml={1}
                        mt={1}
                      >
                        {row.targetStartPage
                          ? `Will map to pages ${
                              sortedPages.length > 1
                                ? `${row.targetStartPage}-${
                                    row.targetStartPage + sortedPages.length - 1
                                  }`
                                : row.targetStartPage
                            }`
                          : ''}
                      </Text>
                    </Box>
                  </Flex>

                  {/* Selectors in same row with equal heights */}
                  <Stack
                    align="stretch"
                    direction={{ base: 'column', xl: 'row' }}
                    height="120px"
                    spacing={4}
                  >
                    <Box flex="1" h="full" minW="250px">
                      <Text
                        color="gray.600"
                        fontSize="xs"
                        fontWeight="semibold"
                        letterSpacing="wide"
                        mb={2}
                        textTransform="uppercase"
                      >
                        Target Document
                      </Text>
                      <DocumentSelect
                        value={row.document ?? undefined}
                        onChange={(opt) =>
                          onUpdateRow(row.id, {
                            document: (opt as DocumentSelectOption) ?? null,
                            version: null,
                          })
                        }
                      />
                    </Box>
                    <Box flex="1" h="full" minW="250px">
                      <Text
                        color="gray.600"
                        fontSize="xs"
                        fontWeight="semibold"
                        letterSpacing="wide"
                        mb={2}
                        textTransform="uppercase"
                      >
                        Target Version
                      </Text>
                      <DocumentVersionSelect
                        documentId={row.document?.value?.id ?? undefined}
                        isDisabled={!row.document}
                        placeholder={
                          row.document
                            ? 'Select version'
                            : 'Select document first'
                        }
                        size="lg"
                        value={row.version ?? null}
                        onChange={(opt) =>
                          onUpdateRow(row.id, {
                            version:
                              (opt as DocumentVersionSelectOption) ?? null,
                          })
                        }
                      />
                    </Box>
                  </Stack>
                </VStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

/**
 * Confirmation dialog shown before applying classification overrides.
 * Displays a summary of the mappings and asks for user confirmation.
 */
interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  mappingRows: MappingRow[];
  totalMappedPages: number;
  isSubmitting: boolean;
}

const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  mappingRows,
  totalMappedPages,
  isSubmitting,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      isCentered
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      size="2xl"
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm Override
          </AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                You are about to override classification for {totalMappedPages}{' '}
                page{totalMappedPages === 1 ? '' : 's'}. The following mappings
                will be applied:
              </Text>
              <Box maxH="40vh" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Source Pages</Th>
                      <Th>Target Document</Th>
                      <Th>Target Pages</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {map(mappingRows, (row) => {
                      const sortedPages = sortBy(row.sourcePages);
                      const start = row.targetStartPage;
                      const end = start
                        ? start + sortedPages.length - 1
                        : undefined;

                      return (
                        <Tr key={row.id}>
                          <Td>
                            {sortedPages.length === 1
                              ? sortedPages[0]
                              : `${sortedPages[0]}-${
                                  sortedPages[sortedPages.length - 1]
                                }`}
                          </Td>
                          <Td>
                            {(() => {
                              const docName =
                                row.document?.value?.name ||
                                row.version?.documentVersion?.document?.name;
                              const versionText =
                                row.version?.documentVersion?.version ||
                                row.version?.label;
                              if (docName && versionText) {
                                return `${docName} - ${versionText}`;
                              }

                              return docName || versionText || 'Unknown';
                            })()}
                          </Td>
                          <Td>
                            {start && end
                              ? start === end
                                ? start
                                : `${start} - ${end}`
                              : '—'}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  This action will re-label the selected ingestion pages to the
                  specified document version pages.
                </AlertDescription>
              </Alert>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              isLoading={isSubmitting}
              ml={3}
              onClick={onConfirm}
            >
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
